import { API } from '@/lib/api';
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Building2, Truck, Users, Package, LogOut, 
  RefreshCw, ChevronRight, Clock, CheckCircle2, XCircle,
  LayoutDashboard, FileText, Download, BarChart3, TrendingUp,
  DollarSign, Eye, FileDown, X, ExternalLink, Star, ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";



// Stat Card Component (moved outside to avoid unstable nested component)
const StatCard = ({ icon: Icon, label, value, pending, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 border border-stone-100"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-stone-500 text-sm mb-1">{label}</p>
        <p className="text-3xl font-bold text-emerald-950">{value}</p>
        {pending !== undefined && (
          <p className="text-sm text-stone-400 mt-1">{pending} pending</p>
        )}
        {trend && (
          <p className={`text-sm mt-1 flex items-center gap-1 ${trend > 0 ? 'text-emerald-600' : 'text-stone-400'}`}>
            <TrendingUp className="w-3 h-3" />
            {trend > 0 ? '+' : ''}{trend} this month
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

// Simple Bar Chart Component (moved outside to avoid unstable nested component)
const SimpleBarChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end justify-between gap-2 h-32">
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center flex-1">
          <div 
            className="w-full bg-lime-400 rounded-t-lg min-h-[4px] transition-all"
            style={{ height: `${(item.count / maxCount) * 100}%` }}
          />
          <span className="text-xs text-stone-500 mt-2">{item.day}</span>
          <span className="text-xs font-medium text-emerald-950">{item.count}</span>
        </div>
      ))}
    </div>
  );
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [b2bLeads, setB2bLeads] = useState([]);
  const [driverApps, setDriverApps] = useState([]);
  const [helperApps, setHelperApps] = useState([]);
  const [cargoBookings, setCargoBookings] = useState([]);
  const [courierBookings, setCourierBookings] = useState([]);
  const [towingBookings, setTowingBookings] = useState([]);
  const [moversBookings, setMoversBookings] = useState([]);
  const [helperBookings, setHelperBookings] = useState([]);
  const [nannyBookings, setNannyBookings] = useState([]);
  const [cleaningBookings, setCleaningBookings] = useState([]);
  const [chefBookings, setChefBookings] = useState([]);
  const [roadsideBookings, setRoadsideBookings] = useState([]);
  const [proApps, setProApps] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [flaggedChats, setFlaggedChats] = useState([]);
  const [moderationFilter, setModerationFilter] = useState("open");
  const [moderationDetail, setModerationDetail] = useState(null);
  const [moderationMessages, setModerationMessages] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const adminName = localStorage.getItem("admin_name") || "Admin";
  const token = localStorage.getItem("admin_token");

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes, driversRes, helpersRes, cargoRes, courierRes, towingRes, moversRes, helperBookingsRes, analyticsRes, nannyRes, cleaningRes, chefRes, roadsideRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, axiosConfig),
        axios.get(`${API}/admin/b2b-leads`, axiosConfig),
        axios.get(`${API}/admin/driver-applications`, axiosConfig),
        axios.get(`${API}/admin/helper-applications`, axiosConfig),
        axios.get(`${API}/admin/cargo-bookings`, axiosConfig),
        axios.get(`${API}/admin/courier-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/towing-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/movers-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/helper-bookings`, axiosConfig),
        axios.get(`${API}/admin/analytics`, axiosConfig).catch(() => ({ data: null })),
        axios.get(`${API}/admin/nanny-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/cleaning-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/chef-bookings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/roadside-bookings`, axiosConfig).catch(() => ({ data: [] }))
      ]);

      // Fetch professional applications separately
      const proAppsRes = await axios.get(`${API}/admin/professional-applications`, axiosConfig).catch(() => ({ data: [] }));

      setStats(statsRes.data);
      setB2bLeads(leadsRes.data);
      setDriverApps(driversRes.data);
      setHelperApps(helpersRes.data);
      setCargoBookings(cargoRes.data);
      setCourierBookings(courierRes.data || []);
      setTowingBookings(towingRes.data || []);
      setMoversBookings(moversRes.data || []);
      setHelperBookings(helperBookingsRes.data);
      setAnalytics(analyticsRes.data);
      setNannyBookings(nannyRes.data || []);
      setCleaningBookings(cleaningRes.data || []);
      setChefBookings(chefRes.data || []);
      setRoadsideBookings(roadsideRes.data || []);
      setProApps(proAppsRes.data || []);

      // Fetch reviews
      const reviewsRes = await axios.get(`${API}/reviews/admin`, axiosConfig).catch(() => ({ data: { reviews: [], stats: {} } }));
      setAdminReviews(reviewsRes.data?.reviews || []);
      setReviewStats(reviewsRes.data?.stats || null);

      // Fetch flagged chats (moderation queue)
      const flaggedRes = await axios.get(`${API}/admin/flagged-chats?status=open`, axiosConfig).catch(() => ({ data: { flagged_chats: [] } }));
      setFlaggedChats(flaggedRes.data?.flagged_chats || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [token, navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const updateStatus = async (type, id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/${type}/${id}/status?status=${newStatus}`, {}, axiosConfig);
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateProAppStatus = async (appId, status) => {
    try {
      await axios.patch(`${API}/admin/professional-applications/${appId}/status?status=${status}`, {}, axiosConfig);
      toast.success(`Application ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update application");
    }
  };

  const banProfessional = async (userId, action) => {
    try {
      await axios.patch(`${API}/admin/professional/${action}/${userId}`, {}, axiosConfig);
      toast.success(`Professional ${action === "ban" ? "banned" : "unbanned"} successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} professional`);
    }
  };

  const allowReapply = async (appId) => {
    try {
      await axios.patch(`${API}/admin/professional-applications/${appId}/allow-reapply`, {}, axiosConfig);
      toast.success("Applicant can now reapply");
      fetchData();
    } catch (error) {
      toast.error("Failed to allow reapply");
    }
  };

  const exportData = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        ...axiosConfig,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.replace('-', ' ')} exported successfully`);
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      under_review: "bg-blue-100 text-blue-700",
      approved: "bg-green-100 text-green-700",
      confirmed: "bg-green-100 text-green-700",
      in_progress: "bg-indigo-100 text-indigo-700",
      completed: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700",
      cancelled: "bg-red-100 text-red-700",
      banned: "bg-red-200 text-red-800",
      reapply_allowed: "bg-blue-100 text-blue-700",
      contacted: "bg-purple-100 text-purple-700"
    };
    return styles[status] || "bg-stone-100 text-stone-700";
  };

  const viewDocuments = (applicant, docs) => {
    setSelectedApplicant(applicant);
    setSelectedDocs(docs);
  };

  const closeDocumentsModal = () => {
    setSelectedApplicant(null);
    setSelectedDocs(null);
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      drivers_license: "Driver's License",
      vehicle_registration: "Vehicle Registration",
      insurance: "Insurance Certificate",
      id_card: "Government ID",
      work_permit: "Work Permit",
      certification: "Certification",
      other: "Other Document"
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-dashboard-page">
      {/* Documents Modal */}
      {selectedDocs && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-stone-100">
              <div>
                <h3 className="font-semibold text-emerald-950">Uploaded Documents</h3>
                <p className="text-sm text-stone-500">{selectedApplicant}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeDocumentsModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-3 overflow-y-auto max-h-[60vh]">
              {selectedDocs.map((doc, index) => (
                <div key={index} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-950">{doc.name || 'Document'}</p>
                      <p className="text-sm text-stone-500">{getDocTypeLabel(doc.type)}</p>
                    </div>
                  </div>
                  <a
                    href={`${doc.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    View
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
              {selectedDocs.length === 0 && (
                <p className="text-center text-stone-500 py-8">No documents uploaded</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-emerald-950 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_deliverstack/artifacts/2v9gvns2_EB4BFD0D-4C9D-43F2-B0D8-4B4D0DF14E28_1_201_a.jpeg" 
                alt="Stack Logo" 
                className="h-10 w-auto rounded-lg"
              />
            </Link>
            <span className="text-stone-400">|</span>
            <span className="text-stone-300">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-stone-300">Welcome, {adminName}</span>
            <Button
              variant="ghost"
              className="text-stone-300 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Refresh & Export Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-emerald-950">Dashboard Overview</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Building2}
              label="B2B Leads"
              value={stats.total_b2b_leads}
              pending={stats.pending_b2b_leads}
              color="bg-emerald-500"
              trend={analytics?.b2b_leads?.this_month}
            />
            <StatCard
              icon={Truck}
              label="Driver Applications"
              value={stats.total_driver_applications}
              pending={stats.pending_driver_applications}
              color="bg-lime-500"
              trend={analytics?.applications?.drivers?.this_month}
            />
            <StatCard
              icon={Users}
              label="Helper Applications"
              value={stats.total_helper_applications}
              pending={stats.pending_helper_applications}
              color="bg-sky-500"
              trend={analytics?.applications?.helpers?.this_month}
            />
            <StatCard
              icon={Package}
              label="Total Bookings"
              value={stats.total_cargo_bookings + stats.total_helper_bookings}
              color="bg-purple-500"
              trend={analytics?.bookings?.this_month}
            />
          </div>
        )}

        {/* Revenue Card */}
        {analytics && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-emerald-950 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Revenue Overview
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-stone-500">This Month</p>
                  <p className="text-2xl font-bold text-emerald-600">${analytics.revenue?.this_month?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-emerald-950">${analytics.revenue?.total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-emerald-950 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-lime-500" />
                  Bookings (Last 7 Days)
                </h3>
              </div>
              <SimpleBarChart data={analytics.daily_bookings} />
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl mb-6 flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="b2b" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="b2b-tab">
              <Building2 className="w-4 h-4 mr-2" />
              B2B Leads ({b2bLeads.length})
            </TabsTrigger>
            <TabsTrigger value="drivers" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="drivers-tab">
              <Truck className="w-4 h-4 mr-2" />
              Drivers ({driverApps.length})
            </TabsTrigger>
            <TabsTrigger value="helpers" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="helpers-tab">
              <Users className="w-4 h-4 mr-2" />
              Helpers ({helperApps.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="bookings-tab">
              <FileText className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="professionals" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="professionals-tab">
              <Users className="w-4 h-4 mr-2" />
              Professionals ({proApps.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-emerald-950 data-[state=active]:text-white" data-testid="reviews-tab">
              <Star className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-lg data-[state=active]:bg-rose-700 data-[state=active]:text-white" data-testid="moderation-tab">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Moderation {flaggedChats.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">{flaggedChats.length}</span>}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent B2B Leads */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-emerald-950">Recent B2B Leads</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("b2b")}>
                    View all <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                {b2bLeads.slice(0, 5).map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                    <div>
                      <p className="font-medium text-emerald-950">{lead.business_name}</p>
                      <p className="text-sm text-stone-500">{lead.contact_person}</p>
                    </div>
                    <Badge className={getStatusBadge(lead.status)}>{lead.status}</Badge>
                  </div>
                ))}
                {b2bLeads.length === 0 && (
                  <p className="text-stone-500 text-center py-8">No B2B leads yet</p>
                )}
              </div>

              {/* Recent Applications */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-emerald-950">Recent Applications</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("drivers")}>
                    View all <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                {[...driverApps, ...helperApps]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 5)
                  .map((app) => (
                    <div key={app.id} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${app.vehicle_type ? 'bg-lime-100' : 'bg-sky-100'}`}>
                          {app.vehicle_type ? (
                            <Truck className="w-4 h-4 text-lime-600" />
                          ) : (
                            <Users className="w-4 h-4 text-sky-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-emerald-950">{app.name}</p>
                          <p className="text-sm text-stone-500">{app.city}</p>
                        </div>
                      </div>
                      <Badge className={getStatusBadge(app.status)}>{app.status.replace('_', ' ')}</Badge>
                    </div>
                  ))}
                {driverApps.length === 0 && helperApps.length === 0 && (
                  <p className="text-stone-500 text-center py-8">No applications yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Bookings by Type */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100">
                <h3 className="font-semibold text-emerald-950 mb-4">Bookings by Service Type</h3>
                {analytics?.bookings_by_type && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.entries(analytics.bookings_by_type).map(([type, data]) => (
                      <div key={type} className="bg-stone-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-emerald-950">{data.total}</p>
                        <p className="text-sm text-stone-500 capitalize">{type}</p>
                        <p className="text-xs text-lime-600 mt-1">+{data.this_month} this month</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Applications Breakdown */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-semibold text-emerald-950 mb-4">Driver Applications</h3>
                  {analytics?.applications?.drivers && (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-stone-500">Total Applications</span>
                        <span className="font-semibold">{analytics.applications.drivers.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">Pending Review</span>
                        <span className="font-semibold text-yellow-600">{analytics.applications.drivers.pending}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">Approved</span>
                        <span className="font-semibold text-green-600">{analytics.applications.drivers.approved}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">This Month</span>
                        <span className="font-semibold text-emerald-600">+{analytics.applications.drivers.this_month}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-semibold text-emerald-950 mb-4">Helper Applications</h3>
                  {analytics?.applications?.helpers && (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-stone-500">Total Applications</span>
                        <span className="font-semibold">{analytics.applications.helpers.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">Pending Review</span>
                        <span className="font-semibold text-yellow-600">{analytics.applications.helpers.pending}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">Approved</span>
                        <span className="font-semibold text-green-600">{analytics.applications.helpers.approved}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-500">This Month</span>
                        <span className="font-semibold text-emerald-600">+{analytics.applications.helpers.this_month}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Export Section */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <FileDown className="w-5 h-5" />
                  Export Data
                </h3>
                <p className="text-stone-500 text-sm mb-4">Download your data as CSV files for reporting and analysis.</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => exportData('b2b-leads')}>
                    <Download className="w-4 h-4 mr-2" />
                    B2B Leads
                  </Button>
                  <Button variant="outline" onClick={() => exportData('driver-applications')}>
                    <Download className="w-4 h-4 mr-2" />
                    Driver Applications
                  </Button>
                  <Button variant="outline" onClick={() => exportData('helper-applications')}>
                    <Download className="w-4 h-4 mr-2" />
                    Helper Applications
                  </Button>
                  <Button variant="outline" onClick={() => exportData('bookings')}>
                    <Download className="w-4 h-4 mr-2" />
                    All Bookings
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* B2B Leads Tab */}
          <TabsContent value="b2b">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportData('b2b-leads')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="b2b-leads-table">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Business</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Contact</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Industry</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Volume</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Status</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {b2bLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-4 px-6">
                        <p className="font-medium text-emerald-950">{lead.business_name}</p>
                        <p className="text-sm text-stone-500">{lead.email}</p>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-emerald-950">{lead.contact_person}</p>
                        <p className="text-sm text-stone-500">{lead.phone}</p>
                      </td>
                      <td className="py-4 px-6 text-stone-600">{lead.industry_type || '-'}</td>
                      <td className="py-4 px-6 text-stone-600">{lead.monthly_volume || '-'}</td>
                      <td className="py-4 px-6">
                        <Badge className={getStatusBadge(lead.status)}>{lead.status}</Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => updateStatus('b2b-leads', lead.id, 'contacted')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => updateStatus('b2b-leads', lead.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {b2bLeads.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>No B2B leads yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportData('driver-applications')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="drivers-table">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Driver</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Contact</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">City</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Vehicle</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Docs</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Status</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {driverApps.map((app) => (
                    <tr key={app.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-4 px-6">
                        <p className="font-medium text-emerald-950">{app.name}</p>
                        <p className="text-sm text-stone-500">{app.email}</p>
                      </td>
                      <td className="py-4 px-6 text-stone-600">{app.phone}</td>
                      <td className="py-4 px-6 text-stone-600">{app.city}</td>
                      <td className="py-4 px-6 text-stone-600">{app.vehicle_type}</td>
                      <td className="py-4 px-6">
                        {app.documents?.length > 0 ? (
                          <button
                            onClick={() => viewDocuments(app.name, app.documents)}
                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                          >
                            <FileText className="w-3 h-3" />
                            {app.documents.length} Doc{app.documents.length > 1 ? 's' : ''}
                            <Eye className="w-3 h-3 ml-1" />
                          </button>
                        ) : (
                          <span className="text-stone-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={getStatusBadge(app.status)}>{app.status.replace('_', ' ')}</Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => updateStatus('driver-applications', app.id, 'approved')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => updateStatus('driver-applications', app.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {driverApps.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  <Truck className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>No driver applications yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Helpers Tab */}
          <TabsContent value="helpers">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => exportData('helper-applications')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="helpers-table">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Helper</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Contact</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">City</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Skills</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Docs</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Status</th>
                    <th className="text-left py-4 px-6 font-semibold text-emerald-950">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {helperApps.map((app) => (
                    <tr key={app.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-4 px-6">
                        <p className="font-medium text-emerald-950">{app.name}</p>
                        <p className="text-sm text-stone-500">{app.email}</p>
                      </td>
                      <td className="py-4 px-6 text-stone-600">{app.phone}</td>
                      <td className="py-4 px-6 text-stone-600">{app.city}</td>
                      <td className="py-4 px-6 text-stone-600 text-sm">{app.skills?.join(', ') || '-'}</td>
                      <td className="py-4 px-6">
                        {app.documents?.length > 0 ? (
                          <button
                            onClick={() => viewDocuments(app.name, app.documents)}
                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                          >
                            <FileText className="w-3 h-3" />
                            {app.documents.length} Doc{app.documents.length > 1 ? 's' : ''}
                            <Eye className="w-3 h-3 ml-1" />
                          </button>
                        ) : (
                          <span className="text-stone-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={getStatusBadge(app.status)}>{app.status.replace('_', ' ')}</Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => updateStatus('helper-applications', app.id, 'approved')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => updateStatus('helper-applications', app.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {helperApps.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>No helper applications yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="flex justify-between items-center mb-4">
              <Button onClick={() => navigate('/admin/bookings')} className="bg-emerald-950 text-white hover:bg-emerald-900" data-testid="view-all-bookings-btn">
                <Package className="w-4 h-4 mr-2" />
                View All Bookings (Uber-Style)
              </Button>
              <Button variant="outline" onClick={() => exportData('bookings')}>
                <Download className="w-4 h-4 mr-2" />
                Export All Bookings
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Cargo Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="cargo-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-lime-500" />
                  Cargo Bookings ({cargoBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {cargoBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        {booking.item_type} ({booking.item_size})
                      </p>
                      <p className="text-sm text-stone-500">
                        From: {booking.pickup_address}
                      </p>
                      <p className="text-sm text-stone-500">
                        To: {booking.dropoff_address}
                      </p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min} - ${booking.estimated_price_max}
                      </p>
                    </div>
                  ))}
                  {cargoBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No cargo bookings yet</p>
                  )}
                </div>
              </div>

              {/* Courier Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="courier-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-sky-500" />
                  Courier Bookings ({courierBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {courierBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        {booking.package_size} - {booking.urgency}
                      </p>
                      <p className="text-sm text-stone-500">From: {booking.pickup_address}</p>
                      <p className="text-sm text-stone-500">To: {booking.dropoff_address}</p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min} - ${booking.estimated_price_max}
                      </p>
                    </div>
                  ))}
                  {courierBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No courier bookings yet</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Towing Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="towing-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-orange-500" />
                  Towing Bookings ({towingBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {towingBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        {booking.vehicle_make_model} - {booking.towing_reason}
                      </p>
                      <p className="text-sm text-stone-500">From: {booking.pickup_address}</p>
                      <p className="text-sm text-stone-500">To: {booking.dropoff_address}</p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min} - ${booking.estimated_price_max}
                      </p>
                    </div>
                  ))}
                  {towingBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No towing bookings yet</p>
                  )}
                </div>
              </div>

              {/* Movers Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="movers-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-500" />
                  Movers Bookings ({moversBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {moversBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        {booking.home_size} home
                      </p>
                      <p className="text-sm text-stone-500">From: {booking.pickup_address}</p>
                      <p className="text-sm text-stone-500">To: {booking.dropoff_address}</p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min} - ${booking.estimated_price_max}
                      </p>
                    </div>
                  ))}
                  {moversBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No movers bookings yet</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Helper/Laborer Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="helper-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-rose-500" />
                  Laborer Bookings ({helperBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {helperBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        Service: {booking.service_type}
                      </p>
                      <p className="text-sm text-stone-500">
                        Address: {booking.service_address}
                      </p>
                      <p className="text-sm text-stone-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {booking.hours_needed} hours needed
                      </p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min} - ${booking.estimated_price_max}
                      </p>
                    </div>
                  ))}
                  {helperBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No laborer bookings yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* AI Chat Bookings Section */}
            <h3 className="font-semibold text-emerald-950 mt-8 mb-4 text-lg">Roadside Assistance Bookings</h3>
            <div className="grid md:grid-cols-1 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="roadside-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-500" />
                  Roadside Assistance ({roadsideBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {roadsideBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Service:</strong> {booking.service_type}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Vehicle:</strong> {booking.vehicle_make_model}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Location:</strong> {booking.location}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Phone:</strong> {booking.customer_phone}
                      </p>
                      {booking.notes && (
                        <p className="text-sm text-stone-500 mb-1">
                          <strong>Notes:</strong> {booking.notes}
                        </p>
                      )}
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.service_price}
                      </p>
                    </div>
                  ))}
                  {roadsideBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No roadside assistance bookings yet</p>
                  )}
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-emerald-950 mt-8 mb-4 text-lg">AI-Powered Service Bookings</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Nanny Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="nanny-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-500" />
                  Nanny Bookings ({nannyBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {nannyBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Email:</strong> {booking.customer_email}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Phone:</strong> {booking.customer_phone}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Address:</strong> {booking.location}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        Children: {booking.number_of_children} ({booking.children_ages})
                      </p>
                      <p className="text-sm text-stone-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {booking.service_date} at {booking.service_time} ({booking.duration_hours}h)
                      </p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  ))}
                  {nannyBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No nanny bookings yet</p>
                  )}
                </div>
              </div>

              {/* Cleaning Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="cleaning-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-cyan-500" />
                  Cleaning Bookings ({cleaningBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {cleaningBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Email:</strong> {booking.customer_email}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Phone:</strong> {booking.customer_phone}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Address:</strong> {booking.location}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        Type: {booking.cleaning_type} ({booking.property_type})
                      </p>
                      <p className="text-sm text-stone-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {booking.service_date} at {booking.service_time}
                      </p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  ))}
                  {cleaningBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No cleaning bookings yet</p>
                  )}
                </div>
              </div>

              {/* Private Chef Bookings */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="chef-bookings-list">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-500" />
                  Private Chef Bookings ({chefBookings.length})
                </h3>
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {chefBookings.map((booking) => (
                    <div key={booking.id} className="border border-stone-100 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-emerald-950">{booking.customer_name}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Email:</strong> {booking.customer_email}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Phone:</strong> {booking.customer_phone}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        <strong>Address:</strong> {booking.service_address}
                      </p>
                      <p className="text-sm text-stone-500 mb-1">
                        Cuisine: {booking.cuisine} | Guests: {booking.guests}
                      </p>
                      <p className="text-sm text-stone-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {booking.event_date} at {booking.event_time}
                      </p>
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        ${booking.estimated_price_min?.toFixed(2) || 'N/A'} - ${booking.estimated_price_max?.toFixed(2) || 'N/A'}
                      </p>
                    </div>
                  ))}
                  {chefBookings.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No chef bookings yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Professionals Tab */}
          <TabsContent value="professionals">
            <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="pro-apps-list">
              <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Professional Verification Applications ({proApps.length})
              </h3>
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {proApps.map((app) => (
                  <div key={app.id} className="border border-stone-100 rounded-xl p-5" data-testid={`pro-app-${app.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium text-emerald-950 text-lg">{app.name}</p>
                        <p className="text-sm text-stone-500">{app.email} | {app.phone}</p>
                      </div>
                      <Badge className={getStatusBadge(app.status)}>{app.status}</Badge>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-stone-400 mb-1">SERVICES</p>
                      <div className="flex flex-wrap gap-1">
                        {app.services?.map(s => (
                          <Badge key={s} variant="outline" className="text-xs capitalize">{s.replace("_", " ")}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-stone-400 mb-1">DOCUMENTS ({app.documents?.length || 0})</p>
                      <div className="flex flex-wrap gap-2">
                        {app.documents?.map((doc, i) => (
                          <a key={i} href={doc.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded">
                            {doc.type?.replace("_", " ")}
                          </a>
                        ))}
                        {app.selfie_url && (
                          <a href={app.selfie_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded">
                            Selfie
                          </a>
                        )}
                      </div>
                    </div>
                    {app.experience && (
                      <div className="mb-3">
                        <p className="text-xs text-stone-400 mb-1">EXPERIENCE</p>
                        <p className="text-sm text-stone-600">{app.experience}</p>
                      </div>
                    )}
                    <p className="text-xs text-stone-400 mb-3">Applied: {new Date(app.created_at).toLocaleString()}</p>
                    {app.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => updateProAppStatus(app.id, 'approved')} data-testid={`approve-pro-${app.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateProAppStatus(app.id, 'rejected')} data-testid={`reject-pro-${app.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                    {app.status === "approved" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => banProfessional(app.user_id, 'ban')} data-testid={`ban-pro-${app.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Ban Professional
                        </Button>
                      </div>
                    )}
                    {app.status === "banned" && (
                      <div className="flex gap-2">
                        <Badge className="bg-red-100 text-red-700 mr-2">Banned</Badge>
                        <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => banProfessional(app.user_id, 'unban')} data-testid={`unban-pro-${app.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Unban
                        </Button>
                      </div>
                    )}
                    {app.status === "rejected" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => allowReapply(app.id)} data-testid={`allow-reapply-${app.id}`}>
                          <RefreshCw className="w-4 h-4 mr-1" /> Allow Reapply
                        </Button>
                      </div>
                    )}
                    {app.status === "reapply_allowed" && (
                      <Badge className="bg-blue-100 text-blue-700">Reapply Allowed</Badge>
                    )}
                  </div>
                ))}
                {proApps.length === 0 && (
                  <p className="text-stone-500 text-center py-8">No professional applications yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" data-testid="reviews-tab-content">
            <div className="space-y-6">
              {/* Stats Cards */}
              {reviewStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <p className="text-xs text-stone-500 mb-1">Average Rating</p>
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <span className="text-2xl font-bold text-emerald-950">{reviewStats.avg_rating || "N/A"}</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <p className="text-xs text-stone-500 mb-1">Total Reviews</p>
                    <span className="text-2xl font-bold text-emerald-950">{reviewStats.total_reviews || 0}</span>
                  </div>
                  <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <p className="text-xs text-stone-500 mb-1">Total Tips</p>
                    <span className="text-2xl font-bold text-emerald-600">${reviewStats.total_tips?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <p className="text-xs text-stone-500 mb-1">Tip Count</p>
                    <span className="text-2xl font-bold text-emerald-950">{reviewStats.tip_count || 0}</span>
                  </div>
                </div>
              )}

              {/* Reviews List */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5" /> All Reviews ({adminReviews.length})
                </h3>
                <div className="space-y-4">
                  {adminReviews.map((review) => (
                    <div key={review.review_id} className="border border-stone-100 rounded-lg p-4" data-testid={`review-${review.review_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-medium text-emerald-950">
                            {review.reviewer_name}
                            <span className="text-xs font-normal text-stone-400 ml-2">({review.reviewer_role})</span>
                          </p>
                          <p className="text-xs text-stone-500">
                            reviewed {review.reviewed_name}
                            <span className="ml-2 capitalize">{review.service_type?.replace("_", " ")}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} className={`w-4 h-4 ${s <= review.rating ? "fill-amber-400 text-amber-400" : "text-stone-200"}`} />
                          ))}
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-stone-600 mb-2 bg-stone-50 rounded-lg p-3">"{review.comment}"</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-stone-400">
                        <span>{new Date(review.created_at).toLocaleString()}</span>
                        {review.tip_amount > 0 && (
                          <span className="text-emerald-600 font-semibold flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Tip: ${review.tip_amount.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {adminReviews.length === 0 && (
                    <p className="text-stone-500 text-center py-8">No reviews yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation" data-testid="moderation-tab-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-emerald-950 flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-rose-600" /> Flagged Chats
                  </h2>
                  <p className="text-sm text-stone-500">Chats auto-flagged by the moderation system (profanity, off-platform contact, repetition).</p>
                </div>
                <div className="flex items-center gap-2">
                  {["open", "reviewed", "dismissed", "all"].map((s) => (
                    <Button key={s} size="sm" variant={moderationFilter === s ? "default" : "outline"} onClick={async () => {
                      setModerationFilter(s);
                      const r = await axios.get(`${API}/admin/flagged-chats?status=${s}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { flagged_chats: [] } }));
                      setFlaggedChats(r.data?.flagged_chats || []);
                    }} className={moderationFilter === s ? "bg-rose-600 hover:bg-rose-700 text-white" : ""} data-testid={`mod-filter-${s}`}>{s}</Button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100" data-testid="flagged-chats-list">
                {flaggedChats.length === 0 ? (
                  <div className="p-10 text-center text-stone-500">
                    <ShieldAlert className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <p>No flagged chats in this view.</p>
                  </div>
                ) : (
                  flaggedChats.map((fc) => (
                    <div key={fc.request_id} className="p-4 flex items-start gap-4 hover:bg-stone-50 transition-colors" data-testid={`flagged-chat-${fc.request_id}`}>
                      <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                        <ShieldAlert className="w-5 h-5 text-rose-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-emerald-950 text-sm">{fc.customer_name || "Customer"} ↔ {fc.professional_name || "Professional"}</span>
                          <Badge variant="outline" className="text-[10px] capitalize">{fc.service_type?.replace("_", " ") || "—"}</Badge>
                          <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">{fc.flag_count}× flagged</Badge>
                          {fc.status && fc.status !== "open" && <Badge variant="outline" className="text-[10px] capitalize">{fc.status}</Badge>}
                        </div>
                        <p className="text-xs text-stone-600 truncate mb-1">"{fc.last_message_preview}"</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {(fc.last_flag_reasons || []).map((r) => (
                            <span key={r} className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full capitalize">{r.replace("_", " ")}</span>
                          ))}
                          <span className="text-[10px] text-stone-400 ml-auto">{new Date(fc.last_flagged_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" onClick={async () => {
                          const r = await axios.get(`${API}/admin/flagged-chats/${fc.request_id}/messages`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { messages: [] } }));
                          setModerationDetail(fc);
                          setModerationMessages(r.data?.messages || []);
                        }} data-testid={`view-chat-${fc.request_id}`}><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
                        {fc.status === "open" && (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" onClick={async () => {
                              await axios.patch(`${API}/admin/flagged-chats/${fc.request_id}/status?status=reviewed`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success("Marked as reviewed");
                              setFlaggedChats((prev) => prev.filter((x) => x.request_id !== fc.request_id));
                            }} data-testid={`review-chat-${fc.request_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Reviewed</Button>
                            <Button size="sm" variant="outline" className="text-stone-600" onClick={async () => {
                              await axios.patch(`${API}/admin/flagged-chats/${fc.request_id}/status?status=dismissed`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              toast.info("Dismissed");
                              setFlaggedChats((prev) => prev.filter((x) => x.request_id !== fc.request_id));
                            }} data-testid={`dismiss-chat-${fc.request_id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Dismiss</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Moderation Detail Modal */}
            {moderationDetail && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModerationDetail(null)} data-testid="moderation-detail-modal">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-emerald-950">Chat Review</h3>
                      <p className="text-xs text-stone-500">{moderationDetail.customer_name} ↔ {moderationDetail.professional_name} · {moderationDetail.service_type?.replace("_", " ")}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setModerationDetail(null)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-stone-50">
                    {moderationMessages.length === 0 ? (
                      <p className="text-center text-stone-400 text-sm py-8">No messages</p>
                    ) : moderationMessages.map((m) => (
                      <div key={m.message_id} className={`flex ${m.sender_role === "professional" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.sender_role === "professional" ? "bg-emerald-600 text-white" : "bg-white border border-stone-200 text-emerald-950"}`}>
                          <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</p>
                          <p>{m.message}</p>
                          {m.moderation_flags?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {m.moderation_flags.map((f) => <span key={f} className="text-[9px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">{f.replace("_", " ")}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
